import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { PhotoPicker, type PickedPhoto } from '@/components/mobile/PhotoPicker';
import { getAxiosErrorMessage, getErrorMessage, isApiValidationError } from '@/core/api/response-handler';
import { StepDone } from '@/domains/collab/components/StepDone';
import { lessonsAPI, uploadAPI, FileFolder } from '../../api';
import { useLessonQuery, useLessonsQuery, lessonKeys } from '../../hooks/useLessonsQueries';
import { CampClase } from '../completion/components/CampClase';
import {
  CONFIG_DEPUNERE,
  PRACTICAL_EVIDENCE_LIMITS,
  canSubmitEvidence,
  esteFisierPdf,
  evidenceBody,
  provocareDejaDepusa,
  stareProvocareText,
  termenExpirat,
  validaPdfDovada,
  type TipDepunere,
} from './practical-evidence';
import { PracticalPageShell } from './PracticalPageShell';

const EROARE_DESCRIERE_ID = 'dovezi-descriere-eroare';
const EROARE_CLASE_ID = 'dovezi-clase-eroare';
const EROARE_TRIMITERE_ID = 'dovezi-trimitere-eroare';
const TERMEN_ID = 'dovezi-termen-expirat';

const INDICATIE_ID = 'dovezi-indicatie';

/**
 * Upload-ul autentificat existent, ca in ImageUploadCompletion: validare locala, apoi URL-ul urcat.
 * Task 15: si PDF (validat pe tip + 15 MB; `compressImageIfNeeded` sare peste non-imagini). Un refuz al
 * serverului (400, ex. continut care nu e PDF/poza) ajunge pe tile-ul fisierului cu mesajul serverului,
 * nu cu textul tehnic al lui axios.
 */
async function urcaDovada(file: File, onProgress: (pct: number) => void): Promise<{ fileId: string }> {
  const validation = esteFisierPdf(file) ? validaPdfDovada(file) : uploadAPI.validateImage(file);
  if (!validation.valid) throw new Error(validation.error ?? 'Fișier invalid');
  try {
    const url = await uploadAPI.uploadImage(file, FileFolder.LESSON_ANSWER_PHOTOS, onProgress);
    return { fileId: url };
  } catch (err) {
    throw new Error(getAxiosErrorMessage(err, 'Încărcarea a eșuat. Încearcă din nou.'));
  }
}

/**
 * B3: „Încarcă poze" — profesorul depune pozele (sau PDF-urile) unei activitati practice intr-un singur ecran,
 * fara parcursul de lectie. Doar PROFESOR (ruta si endpoint-ul). Dupa trimitere poate retrimite
 * oricand („Trimite încă una"); fiecare trimitere e o depunere noua, in aprobare.
 */
export default function PracticalEvidencePage({ tip = 'PRACTICAL_ACTIVITY' }: { tip?: TipDepunere }) {
  const cfg = CONFIG_DEPUNERE[tip];
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: lesson, isLoading, isError, error, refetch, isFetching } = useLessonQuery(id);
  // Provocarea pentru scoala: daca scoala a trimis deja, nu lasam profesorul sa urce poze pentru un
  // 409 sigur. Starea vine din lista (contoare pe scoala).
  const lista = useLessonsQuery(tip, undefined, { enabled: cfg.oSinguraDepunere });
  const dinLista = cfg.oSinguraDepunere ? lista.data?.find((l) => l.id === id) : undefined;
  const dejaDepusa = provocareDejaDepusa(dinLista?.depuneriInAsteptare, dinLista?.depuneriAprobate);
  const stareProvocare = stareProvocareText(dinLista?.depuneriInAsteptare, dinLista?.depuneriAprobate);

  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  const [clase, setClase] = useState('');
  const [claseAtinse, setClaseAtinse] = useState(false);
  const [description, setDescription] = useState('');
  const [descriereAtinsa, setDescriereAtinsa] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fragmenteRespinse, setFragmenteRespinse] = useState<string[] | undefined>();
  const [eroareServer, setEroareServer] = useState<string | null>(null);
  const [trimise, setTrimise] = useState<number | null>(null);
  // Garda sincrona contra dublu-tap: starea `submitting` se vede abia la urmatorul render, ref-ul imediat.
  const inCursRef = useRef(false);
  // Dupa „Trimite încă una" formularul revine, iar butonul apasat (din StepDone) dispare: focusul ar
  // cadea pe `body`. Il mutam pe titlul paginii, ca omul (si cititorul de ecran) sa stie unde e.
  const titluRef = useRef<HTMLHeadingElement>(null);
  const focusTitluRef = useRef(false);
  useEffect(() => {
    if (trimise === null && focusTitluRef.current) {
      focusTitluRef.current = false;
      titluRef.current?.focus();
    }
  }, [trimise]);

  // 400 cu fragmente necunoscute: eroarea sta in CampClase, deasupra pliului pe telefon. Omul a apasat
  // „Trimite" jos, deci il ducem la camp (focus + derulare), nu doar il anuntam.
  useEffect(() => {
    if (!fragmenteRespinse?.length) return;
    const camp = document.getElementById('camp-clase');
    camp?.focus({ preventScroll: true });
    camp?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [fragmenteRespinse]);

  // Final review M1: termenul se stie din GET-ul lectiei; nu lasam profesorul sa urce 5 poze pe date
  // mobile pentru un 409 sigur. Serverul refuza oricum (DepunereProfesorService).
  const expirat = termenExpirat(lesson?.deadline);
  const blocat = submitting || expirat;
  const poateTrimite = !expirat && canSubmitEvidence({ photos, clase, submitting, description }, cfg);
  const claseLipsa = cfg.cereClase && claseAtinse && clase.trim() === '';
  const descriereLipsa = cfg.cereDescriere && descriereAtinsa && description.trim() === '';

  const reset = () => {
    focusTitluRef.current = true;
    setPhotos([]);
    setClase('');
    setClaseAtinse(false);
    setDescription('');
    setDescriereAtinsa(false);
    setFragmenteRespinse(undefined);
    setEroareServer(null);
    setTrimise(null);
  };

  const trimite = async () => {
    // Garda si aici, nu doar pe buton: un dublu-tap poate ajunge inainte ca butonul sa se dezactiveze.
    if (!poateTrimite || inCursRef.current) return;
    inCursRef.current = true;
    setSubmitting(true);
    setEroareServer(null);
    setFragmenteRespinse(undefined);
    try {
      const body = evidenceBody({ photos, clase, description }, cfg.cereClase);
      await lessonsAPI.submitPracticalEvidence(id, body);
      // Linia de stare de pe card („Trimisă de N ori · …") vine din listare: o reimprospatam.
      queryClient.invalidateQueries({ queryKey: lessonKeys.list(tip) });
      setTrimise(body.photoUrls.length);
    } catch (err) {
      if (isApiValidationError(err) && err.fragmenteNerecunoscute?.length) {
        setFragmenteRespinse(err.fragmenteNerecunoscute);
      } else {
        setEroareServer(getErrorMessage(err, 'Trimiterea nu a reușit. Încearcă din nou.'));
      }
    } finally {
      inCursRef.current = false;
      setSubmitting(false);
    }
  };

  let continut;
  if (isLoading) {
    continut = (
      <div className="space-y-4" aria-busy="true" aria-label={cfg.texte.seIncarca}>
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-5 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  } else if (isError) {
    continut = (
      <div role="alert" className="space-y-4">
        <p className="text-base text-pretty text-destructive">
          {getErrorMessage(error, cfg.texte.neincarcata)}
        </p>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          Încearcă din nou
        </Button>
      </div>
    );
  } else if (!lesson || lesson.type !== tip || lesson.isHidden) {
    continut = <p className="text-base text-pretty">{cfg.texte.negasita}</p>;
  } else if (trimise !== null) {
    continut = (
      <StepDone
        activityTitle={lesson.title}
        photoCount={trimise}
        // Provocarea are o singura depunere per scoala: fara „Trimite încă una".
        onAnother={cfg.oSinguraDepunere ? undefined : reset}
        titlu={cfg.texte.titluTrimis}
        focusTitleOnMount
        note={cfg.texte.notaTrimis}
        secondaryAction={
          <Button
            type="button"
            variant={cfg.oSinguraDepunere ? 'default' : 'outline'}
            className="min-h-12 w-full"
            onClick={() => navigate(cfg.lista)}
          >
            {cfg.texte.inapoiLaLista}
          </Button>
        }
      />
    );
  } else if (dejaDepusa) {
    continut = (
      <div className="space-y-4" data-provocare-deja-depusa>
        <h1 className="text-fluid-2xl font-bold leading-tight text-balance">{lesson.title}</h1>
        <p role="status" className="text-base font-medium">
          {stareProvocare}
        </p>
        <p className="text-base text-pretty text-muted-foreground">
          Școala ta a trimis deja dovezile pentru această provocare. Nu mai e nevoie de o a doua trimitere.
        </p>
        <Button type="button" className="min-h-12 w-full" onClick={() => navigate(cfg.lista)}>
          {cfg.texte.inapoiLaLista}
        </Button>
      </div>
    );
  } else {
    continut = (
      <form
        className="space-y-6"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void trimite();
        }}
      >
        <div className="space-y-1">
          <h1 ref={titluRef} tabIndex={-1} className="text-fluid-2xl font-bold leading-tight text-balance outline-none">
            {cfg.texte.titluIncarcare}
          </h1>
          <p className="text-base text-pretty text-muted-foreground">{lesson.title}</p>
        </div>

        {expirat && (
          <p id={TERMEN_ID} role="status" className="font-medium text-warning">
            {cfg.texte.termenExpirat}
          </p>
        )}

        <div className="space-y-2">
          <PhotoPicker
            photos={photos}
            onChange={setPhotos}
            upload={urcaDovada}
            max={PRACTICAL_EVIDENCE_LIMITS.MAX}
            disabled={blocat}
            acceptPdf
          />
          <p id={INDICATIE_ID} className="text-sm text-pretty text-muted-foreground">
            Poze sau PDF, maximum {PRACTICAL_EVIDENCE_LIMITS.MAX}.
          </p>
        </div>

        {/* onBlur urca din Input: eroarea „obligatoriu" apare dupa ce omul a trecut prin camp, nu din prima. */}
        {cfg.cereClase && (
        <div className="space-y-2" onBlur={() => setClaseAtinse(true)}>
          <CampClase
            value={clase}
            onChange={setClase}
            disabled={blocat}
            fragmenteRespinse={fragmenteRespinse}
            label="Cu ce clase ați făcut activitatea?"
            errorId={claseLipsa ? EROARE_CLASE_ID : undefined}
          />
          {claseLipsa && (
            <p id={EROARE_CLASE_ID} className="text-sm text-error">
              Scrie cel puțin o clasă.
            </p>
          )}
        </div>
        )}

        <div className="space-y-2">
          <label htmlFor="dovezi-descriere" className="block font-medium text-foreground">
            {cfg.texte.etichetaDescriere}
          </label>
          <Textarea
            id="dovezi-descriere"
            value={description}
            maxLength={2000}
            rows={4}
            className="text-base"
            disabled={blocat}
            required={cfg.cereDescriere}
            aria-invalid={descriereLipsa || undefined}
            aria-describedby={descriereLipsa ? EROARE_DESCRIERE_ID : undefined}
            placeholder={cfg.texte.indicatieDescriere}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setDescriereAtinsa(true)}
          />
          {descriereLipsa && (
            <p id={EROARE_DESCRIERE_ID} className="text-sm text-error">
              Scrie câteva cuvinte despre cum s-a desfășurat provocarea.
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Button
            type="submit"
            className="min-h-12 w-full"
            disabled={!poateTrimite}
            aria-busy={submitting || undefined}
            aria-describedby={eroareServer ? EROARE_TRIMITERE_ID : expirat ? TERMEN_ID : undefined}
          >
            {submitting ? 'Se trimite…' : 'Trimite'}
          </Button>
          {eroareServer && (
            <p id={EROARE_TRIMITERE_ID} role="alert" className="text-sm text-pretty text-error">
              {eroareServer}
            </p>
          )}
        </div>
      </form>
    );
  }

  return <PracticalPageShell lista={cfg.lista}>{continut}</PracticalPageShell>;
}
